/**
 * The guest dashboard's rows — handoff §1 "YOUR EVENTS". Pure: takes what the three calls
 * return (my applications, my invitations, my tickets), merges duplicates, splits upcoming
 * from past, and groups upcoming into NEXT UP · NEEDS YOU · INTERESTED · COMING UP. The
 * chip / meta / button words live here too so the page is presentation only.
 */

export const UPCOMING_EVENT_STATUSES = ['draft', 'selling', 'funding', 'processing_greenlight', 'greenlit'];
export const NEEDS_YOU = ['pending_payment', 'payment_failed', 'invited'];
const GOING = ['accepted', 'paid'];

const time = (iso) => (iso ? new Date(iso).getTime() : 0);

/** Upcoming = the date is still ahead, or the event is still in a live status. */
export function isUpcoming(event, now = new Date()) {
  if (!event) return false;
  if (event.dateTime && new Date(event.dateTime) > now) return true;
  return UPCOMING_EVENT_STATUSES.includes(event.status);
}

/**
 * One row per event. An application owns its tickets (matched by application id, then by
 * event id); tickets with no application of yours (ones sent to you) become `ticket` rows,
 * one per event; invitations to events you already applied to are dropped.
 */
export function buildRows({ applications = [], invitations = [], tickets = [] }, now = new Date()) {
  const rows = [];
  const byAppId = new Map();
  const byEventId = new Map();

  for (const app of applications) {
    if (!app?.event) continue;
    const row = { key: `app-${app.id}`, kind: 'application', status: app.status, event: app.event, application: app, tickets: [], ticketCount: 0 };
    rows.push(row);
    byAppId.set(app.id, row);
    if (app.event.id) byEventId.set(app.event.id, row);
  }

  const orphans = new Map();
  for (const t of tickets) {
    const owner = byAppId.get(t.applicationId) || (t.eventId ? byEventId.get(t.eventId) : null);
    if (owner) { owner.tickets.push(t); owner.ticketCount += 1; continue; }
    const k = t.eventId || t.eventTitle;
    if (!k) continue;
    let row = orphans.get(k);
    if (!row) {
      row = {
        key: `ticket-${k}`, kind: 'ticket', status: 'ticket', tickets: [], ticketCount: 0,
        event: {
          id: t.eventId, title: t.eventTitle, dateTime: t.eventDateTime, locationCity: t.eventLocationCity,
          locationAddress: t.eventLocationAddress, posterUrl: t.eventPosterUrl, status: t.eventStatus, inviteCode: t.eventInviteCode,
        },
      };
      orphans.set(k, row);
      rows.push(row);
    }
    row.tickets.push(t);
    row.ticketCount += 1;
  }

  for (const inv of invitations) {
    const ev = inv?.event;
    if (!ev || byEventId.has(ev.id)) continue;
    rows.push({ key: `inv-${ev.id}`, kind: 'invitation', status: 'invited', event: ev, invitedBy: inv.invitedByDisplayName, tickets: [], ticketCount: 0 });
  }

  for (const r of rows) r.upcoming = isUpcoming(r.event, now);
  return rows;
}

/** NEXT UP (soonest you're in) · NEEDS YOU · INTERESTED · COMING UP, all from the upcoming rows. */
export function groupUpcoming(rows) {
  const up = rows.filter((r) => r.upcoming).sort((a, b) => time(a.event?.dateTime) - time(b.event?.dateTime));
  const nextUp = up.find((r) => r.kind === 'application' && GOING.includes(r.status)) || null;
  const rest = up.filter((r) => r !== nextUp);
  const needs = rest.filter((r) => NEEDS_YOU.includes(r.status));
  const interested = rest.filter((r) => r.status === 'external_interest');
  const comingUp = rest.filter((r) => !needs.includes(r) && !interested.includes(r));
  return { nextUp, needs, interested, comingUp, count: up.length };
}

export function pastRows(rows) {
  return rows.filter((r) => !r.upcoming).sort((a, b) => time(b.event?.dateTime) - time(a.event?.dateTime));
}

const GREY = '#F2F2F2';

/** The status chip on an upcoming row. */
export function chipFor(row) {
  switch (row.status) {
    case 'pending_payment': return { label: 'PAY TO CONFIRM', bg: '#FFF3C4', fg: '#000' };
    case 'payment_failed': return { label: 'PAYMENT FAILED', bg: '#FEE2E2', fg: '#000' };
    case 'invited': return { label: 'INVITED', bg: '#E6FFFF', fg: '#000' };
    case 'external_interest': return { label: 'INTERESTED', bg: GREY, fg: '#000' };
    case 'pending': return { label: 'APPLIED', bg: GREY, fg: '#000' };
    case 'accepted':
    case 'paid': return { label: "YOU'RE IN", bg: '#22C55E', fg: '#fff' };
    case 'ticket': return { label: `${row.ticketCount} ${row.ticketCount === 1 ? 'TICKET' : 'TICKETS'}`, bg: GREY, fg: '#000' };
    case 'declined': return { label: 'DECLINED', bg: '#FEE2E2', fg: '#000' };
    case 'waitlisted': return { label: 'WAITLISTED', bg: '#FFF3C4', fg: '#000' };
    default: return { label: (row.status || '').replace(/_/g, ' ').toUpperCase(), bg: GREY, fg: '#000' };
  }
}

/** The chip on a PAST row: what ended it. */
export function pastChip(row) {
  if (row.status === 'declined') return { label: 'DECLINED', bg: '#FEE2E2', fg: '#000' };
  const s = row.event?.status;
  if (s === 'canceled' || s === 'dissolved') return { label: s.toUpperCase(), bg: '#FEE2E2', fg: '#000' };
  return { label: 'ENDED', bg: GREY, fg: '#000' };
}

/** The chip on a SAVED row: the event's own status. */
export function savedChip(status) {
  switch (status) {
    case 'selling': return { label: 'SELLING', bg: '#22C55E', fg: '#fff' };
    case 'funding': return { label: 'FUNDING', bg: '#FF00FF', fg: '#fff' };
    case 'greenlit': return { label: 'GREENLIT', bg: '#22C55E', fg: '#fff' };
    case 'processing_greenlight': return { label: 'GREENLIGHTING', bg: '#FFF3C4', fg: '#000' };
    case 'canceled':
    case 'dissolved': return { label: status.toUpperCase(), bg: '#FEE2E2', fg: '#000' };
    default: return { label: (status || 'DRAFT').toUpperCase(), bg: GREY, fg: '#000' };
  }
}

const up = (s) => (s || '').toUpperCase();
export const placeOf = (event) => up(event?.venueName || event?.locationCity || '');

/** The 9px line under an upcoming row's title. `dark` asks for black text instead of grey. */
export function metaFor(row) {
  const ev = row.event || {};
  const place = placeOf(ev);
  switch (row.status) {
    case 'pending_payment': return { text: 'ACCEPTED · PAY TO LOCK YOUR SPOT', dark: true };
    case 'payment_failed': return { text: 'PAYMENT FAILED · YOUR SPOT IS STILL HELD', dark: true };
    case 'invited': return { text: ['INVITED BY ' + up(row.invitedBy || 'A FRIEND'), place].filter(Boolean).join(' · ') };
    case 'external_interest': return { text: ['EXTERNAL', place].filter(Boolean).join(' · ') };
    default: {
      const bits = [ev.hostName ? `HOSTED BY ${up(ev.hostName)}` : '', place].filter(Boolean);
      return { text: bits.join(' · ') };
    }
  }
}

export function pastMeta(row) {
  const place = placeOf(row.event);
  if (row.status === 'declined') return 'DECLINED BY HOST';
  const attended = row.application?.attendanceStatus === 'attended';
  const went = attended || GOING.includes(row.status) || row.kind === 'ticket';
  return [went ? (attended ? 'ATTENDED' : 'YOU WERE IN') : up(row.status).replace(/_/g, ' '), place].filter(Boolean).join(' · ');
}

/** `17 DAYS` / `1 DAY` / `TODAY` for the next-up clock. */
export function daysTag(iso, now = new Date()) {
  if (!iso) return '';
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const d = new Date(iso);
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const n = Math.round((day - start) / 86400000);
  if (n <= 0) return 'TODAY';
  return n === 1 ? '1 DAY' : `${n} DAYS`;
}

/** `$25.00` from cents. */
export function dollars(cents) {
  return `$${((cents || 0) / 100).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** `FRI OCT 23 · 7:00 PM` — the row's when line. */
export function whenLine(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).replace(',', '');
  const t = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return up(`${day} · ${t}`);
}

/** `12 GOING · 3 FRIENDS` (friends only when the API says). */
export function goingLine(event) {
  const n = event?.acceptedCount || 0;
  const friends = event?.friendsGoingCount;
  const parts = [`${n} GOING`];
  if (friends) parts.push(`${friends} ${friends === 1 ? 'FRIEND' : 'FRIENDS'}`);
  return parts.join(' · ');
}

/** The invite field: pull the code out of `/events/<code>`, `/e/<code>`, `/invite/<code>`, else use it as typed. */
export function extractInviteCode(raw) {
  const s = (raw || '').trim();
  const m = s.match(/\/(?:events?|e|invite)\/([^/?\s]+)/);
  return m ? m[1] : s;
}
